import { parseTracklist, effectiveTarget, itemForPosition } from '@/shared/albumTracklist';

/**
 * What an Album Session is doing right now, worked out from the saved session row the server sends on every poll
 * (AUTO-099). The old panel only appeared after the first track had finished and showed the form's Track Count
 * slider, so a session that had just started looked idle and a count bigger than the brief's list was misleading.
 */
export interface LiveSession {
  enabled: boolean;
  targetCount: number;
  narrativeBlock: string;
  budgetCapUsd: number;
  sessionStartedAt?: string | null;
  sessionCompleted?: number | null;
  sessionFailed?: number | null;
  sessionSpentUsd?: number | null;
  lastRunAt?: string | null;
  lastError?: string | null;
}

export type SessionState = 'running' | 'finished' | 'stopped';

export interface SessionStatus {
  state: SessionState;
  headline: string;
  /** Plain lines under the headline: when it started, last activity, which brief item is being written. */
  lines: string[];
  warning: string | null;
  completed: number;
  /** The number of tracks the session will really make (the smaller of the Track Count and the brief's list). */
  target: number;
  /** "target_reached" / "budget_reached" are normal endings, not errors. */
  endedNormally: boolean | null;
  lastError: string | null;
}

/** A track normally takes about 10 minutes; the server gives up on a run that is still going after 20. */
export const STUCK_AFTER_MINUTES = 20;

const defaultTime = (d: Date): string => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function minutesBetween(fromMs: number, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - fromMs) / 60000));
}

function ago(minutes: number): string {
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  return `${minutes} minutes ago`;
}

function firstLine(text: string, max = 80): string {
  const line = text.split('\n')[0].trim();
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

/** Null when there is nothing to show yet (a session that was never started). */
export function describeSession(live: LiveSession | null, nowMs: number, formatTime: (d: Date) => string = defaultTime): SessionStatus | null {
  if (!live) return null;
  const completed = live.sessionCompleted ?? 0;
  const startedMs = live.sessionStartedAt ? new Date(live.sessionStartedAt).getTime() : null;
  if (!live.enabled && completed === 0 && startedMs === null) return null;

  const tracklist = parseTracklist(live.narrativeBlock);
  const target = effectiveTarget(live.targetCount, tracklist);

  // Switching a session on resets the counters but not lastRunAt, so a time from before this session started is
  // not this session's activity.
  const rawLastMs = live.lastRunAt ? new Date(live.lastRunAt).getTime() : null;
  const lastMs = rawLastMs !== null && (startedMs === null || rawLastMs >= startedMs) ? rawLastMs : null;

  const lines: string[] = [];
  let warning: string | null = null;

  if (startedMs !== null) lines.push(`Started ${formatTime(new Date(startedMs))} (${ago(minutesBetween(startedMs, nowMs))}).`);
  if (lastMs !== null) lines.push(`Last activity ${formatTime(new Date(lastMs))} (${ago(minutesBetween(lastMs, nowMs))}).`);

  if (tracklist) {
    const note = live.targetCount > tracklist.items.length
      ? `Your brief is a numbered list of ${tracklist.items.length} songs and Track Count is ${live.targetCount}, so this session makes ${target}.`
      : target < tracklist.items.length
        ? `Your brief is a numbered list of ${tracklist.items.length} songs; Track Count is ${live.targetCount}, so this session makes the first ${target}.`
        : `Your brief is a numbered list of ${tracklist.items.length} songs; this session makes all ${target}.`;
    lines.push(note);
  }

  if (live.enabled) {
    const next = completed + 1;
    const item = tracklist ? itemForPosition(tracklist, next) : null;
    if (item) lines.push(`Now writing item ${item.n}: ${firstLine(item.text)}`);
    if (completed === 0 && lastMs === null) lines.push('The first track takes about 10 minutes.');

    const sinceMs = lastMs ?? startedMs;
    if (sinceMs !== null) {
      const quiet = minutesBetween(sinceMs, nowMs);
      if (quiet > STUCK_AFTER_MINUTES) {
        warning = `Nothing has happened for ${quiet} minutes. A track normally takes about 10; the server gives up on a stuck one after ${STUCK_AFTER_MINUTES}.`;
      }
    }
    return {
      state: 'running',
      headline: completed >= target ? 'Running — finishing up' : `Running — writing track ${next} of ${target}`,
      lines, warning, completed, target, endedNormally: null, lastError: live.lastError ?? null,
    };
  }

  const endedNormally = live.lastError ? /target_reached|budget_reached/.test(live.lastError) : null;
  const done = `${completed} of ${target} track${target === 1 ? '' : 's'}`;
  return {
    state: endedNormally === false ? 'stopped' : 'finished',
    headline: endedNormally === false ? `Stopped — ${done}` : `Finished — ${done}`,
    lines, warning, completed, target, endedNormally, lastError: live.lastError ?? null,
  };
}
